import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { setAccessToken } from '../utils/api';

export interface User {
  id: number;
  username: string;
  fullName: string;
  role: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  permissionsLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (module: string, permission: string) => boolean;
  hasAnyPermission: (module: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState<any[]>([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    
    // Clear refresh cookie on server first
    try {
      await fetch('/api/auth/logout', { 
        method: 'POST', 
        credentials: 'include' 
      });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    
    // Clear local state
    localStorage.removeItem('token');
    setAccessToken(null);
    setToken(null);
    setUser(null);
    setUserPermissions([]);
    setIsLoggingOut(false);
  }, []);

  const fetchUserPermissions = useCallback(async (userId: number, accessToken?: string) => {
    setPermissionsLoading(true);
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      } else if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/users/${userId}/permissions`, { 
        credentials: 'include', 
        headers 
      });
      
      if (response.status === 401) {
        logout();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        // Combine role permissions and user permissions
        const rolePerms = data.data.rolePermissions.map((p: any) => ({ ...p, granted: true }));
        const userPerms = data.data.userPermissions;
        
        // User permissions override role permissions
        const allPermissions: any[] = [];
        
        // Add role permissions first
        rolePerms.forEach((rolePerm: any) => {
          const userOverride = userPerms.find((up: any) => 
            up.module === rolePerm.module && up.permission === rolePerm.permission
          );
          
          if (userOverride) {
            // Use user permission (can be granted: false to override role)
            allPermissions.push(userOverride);
          } else {
            // Use role permission
            allPermissions.push(rolePerm);
          }
        });
        
        // Add user permissions that don't override role permissions
        userPerms.forEach((userPerm: any) => {
          const existsInRole = rolePerms.some((rp: any) => 
            rp.module === userPerm.module && rp.permission === userPerm.permission
          );
          
          if (!existsInRole) {
            allPermissions.push(userPerm);
          }
        });
        
        setUserPermissions(allPermissions);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [logout, token]);

  useEffect(() => {
    // Don't auto-refresh if logging out
    if (isLoggingOut) {
      setLoading(false);
      return;
    }
    
    // Try to restore token from localStorage first
    (async () => {
      try {
        // First try to get token from localStorage
        const savedToken = localStorage.getItem('token');
        if (savedToken) {
          setAccessToken(savedToken);
          setToken(savedToken);

          // Try to get user info with saved token
          try {
            const userResp = await fetch('/api/auth/me', {
              headers: { 'Authorization': `Bearer ${savedToken}` },
              credentials: 'include'
            });
            if (userResp.ok) {
              const userData = await userResp.json();
              if (userData?.success && userData?.user) {
                setUser(userData.user);
                if (userData.user.id) fetchUserPermissions(userData.user.id, savedToken);
                setLoading(false);
                return;
              }
            }
          } catch (e) {
            console.log('Saved token invalid, trying refresh...');
            localStorage.removeItem('token');
          }
        }

        // If no saved token or it's invalid, try refresh
        const resp = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.success && data?.token && data?.user) {
            localStorage.setItem('token', data.token);
            setAccessToken(data.token);
            setToken(data.token);
            setUser(data.user);
            if (data.user.id) fetchUserPermissions(data.user.id, data.token);
          }
        }
      } catch (error) {
        // No existing session
      }
      setLoading(false);
    })();
  }, [fetchUserPermissions, isLoggingOut]); // Include isLoggingOut dependency

  const login = async (username: string, password: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        localStorage.setItem('token', data.token);
        setAccessToken(data.token);
        setToken(data.token);
        setUser(data.user);
        if (data.user.id) {
          fetchUserPermissions(data.user.id, data.token);
        }
      } else {
        // Handle rate limit errors with detailed information
        if (response.status === 429 && data.lockoutDuration) {
          const errorMsg = `${data.error}\n\n` +
            `• Tối đa: ${data.maxAttempts} lần thử\n` +
            `• Còn lại: ${data.attemptsLeft || 0} lần thử\n` +
            `• Thời gian khóa: ${data.lockoutDuration}\n` +
            `• Thử lại sau: ${data.retryAfter}`;
          throw new Error(errorMsg);
        }
        throw new Error(data.error || 'Đăng nhập thất bại');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Không thể kết nối tới server');
    }
  };


  const hasPermission = (module: string, permission: string): boolean => {
    if (!user) return false;
    
    // Admin has all permissions
    if (user.role === 'admin') return true;
    
    // Check if user has the specific permission
    return userPermissions.some(p => 
      p.module === module && 
      p.permission === permission && 
      p.granted === true
    );
  };

  const hasAnyPermission = (module: string): boolean => {
    if (!user) return false;
    
    // Admin has all permissions
    if (user.role === 'admin') return true;
    
    
    // Check if user has any GRANTED permission for the module
    return userPermissions.some(p => 
      p.module === module && 
      p.granted === true
    );
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user,
    loading,
    permissionsLoading,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};